import React, { useState, useEffect, useMemo } from "react";

import { Table, Select } from "antd";
import { ArrowRightOutlined, DatabaseOutlined, CheckCircleTwoTone, WarningTwoTone } from '@ant-design/icons';

import { getData } from '../../config/api-connect';

import SummaryLayout from '../../components/layout/SummaryLayout'
import MonthSelector from "../../components/ui/MonthSelector";
import { useMonthFilter } from "../../hooks/useMonthFilter";
import { useCityName } from "../../hooks/useCityName";
import { Link } from "react-router-dom";

function CollectionSummary() {
  const { Option } = Select;

  const { selectedMonth, setSelectedMonth } = useMonthFilter();
  const { cityName: city_name, setCityName } = useCityName();
  
  const [summaryLoader, setSummaryLoader] = useState(false)
  const [collectionSummary, setCollectionSummary] = useState([])

  const cityOptions = [
    "Bangalore",
    "Kerala",
    "Hyderabad",
    "Qatar",
    "UAE",
    "Kuwait",
    "Poland",
    "France",
    "Germany",
    "Thailand",
  ];

  useEffect(() => {
    console.log("EFFECT RUNNING");
    get_collection_summary();
  }, [city_name, selectedMonth.format("YYYY-MM")]);

  const get_collection_summary = () => {
    setSummaryLoader(true)
    
    const start_date = selectedMonth.clone().startOf('month').format('YYYY-MM-DD');
    const end_date = selectedMonth.clone().endOf('month').format('YYYY-MM-DD');
    getData(`/finance/get_venue_wise_collection_summary/?city_name=${city_name}&start_date=${start_date}&end_date=${end_date}`)
      .then(response => {
        setCollectionSummary(response.result)
        setSummaryLoader(false)
      })
      .catch(error => {
        setSummaryLoader(false)
        alert(error.message);
      });
  }

  const statsConfig = [
    {
      key: "total_collection",
      title: "System Collection",
      icon: <DatabaseOutlined style={{ fontSize: 35, color: "#722ed1" }} />,
    },
    {
      key: "verified",
      title: "Verified",
      icon: <CheckCircleTwoTone twoToneColor="#1565c0" style={{ fontSize: 35 }} />,
    },
    {
      key: "unverified",
      title: "Unverified",
      icon: <WarningTwoTone twoToneColor="#faad14" style={{ fontSize: 35 }} />,
    },
  ];

  const summaryTotals = useMemo(() => {
    if (!collectionSummary?.length) {
      return {
        total_collection: 0,
        verified: 0,
        unverified: 0,
      };
    }

    return collectionSummary.reduce(
      (acc, r) => {
        acc.total_collection += r.total_collection || 0;
        acc.verified += r.verified || 0;
        acc.unverified += r.unverified || 0;
        return acc;
      },
      {
        total_collection: 0,
        verified: 0,
        unverified: 0,
      }
    );
  }, [collectionSummary]);

  const summaryCells = [
    { key: "total_collection" },
    { key: "verified" },
    { key: "unverified" },
  ];

  const renderSummary = () => (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0}>
        <b>Total</b>
      </Table.Summary.Cell>

      {summaryCells.map((item, i) => (
        <Table.Summary.Cell
          key={item.key}
          index={i + 2}
          align="right"
        >
          <b>{formatNumber(summaryTotals[item.key])}</b>
        </Table.Summary.Cell>
      ))}
      <Table.Summary.Cell />
    </Table.Summary.Row>
  );

  const collection_columns = [
    {
      title: "Venue",
      dataIndex: "venue_name",
      render: (text) => <b>{text}</b>,
    },
    {
      title: "Collection",
      dataIndex: "total_collection",
      align: "right",
      render: (value) => value.toLocaleString("en-IN"),
    },
    {
      title: "Verified",
      dataIndex: "verified",
      align: "right",
      render: (value, record) => {
        const isEqual = value === record.total_collection;

        return (
          <span style={{ color: isEqual ? "green" : "inherit", fontWeight: isEqual ? 600 : "normal" }}>
            {value.toLocaleString("en-IN")}
          </span>
        );
      },
    },
    {
      title: "Unverified",
      dataIndex: "unverified",
      align: "right",
      render: (value, record) => (
        <span
          style={{
            color: value > 0 ? "red" : "inherit",
            fontWeight: value > 0 ? 600 : "normal",
            cursor: record.venue_filter ? "pointer" : "default",
          }}
        >
          {value.toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "",
      dataIndex: "venue_filter",
      align: "center",
      render: (_, record) =>
        <Link to={`/collection-verification/?venue_id=${record.venue_id}&month=${selectedMonth.format('MM')}&year=${selectedMonth.format('YYYY')}`}>
          <ArrowRightOutlined
            style={{ cursor: "pointer", color: "#1565c0" }}
          />
        </Link>
    },
  ];

  const formatNumber = (value) =>
    Math.round(value).toLocaleString("en-IN");

  const renderRightSideActions = () => (
    <>
      <Select
        placeholder="Select City"
        style={{ width: 160 }}
        value={city_name}
        onChange={(value) => setCityName(value)}
      >
        {cityOptions.map((city) => (
          <Option key={city} value={city}>
            {city}
          </Option>
        ))}
      </Select>

      <MonthSelector
        value={selectedMonth}
        onChange={setSelectedMonth}
      />
    </>
  )

  return (
    <SummaryLayout
      title="Collection Verification Summary"
      headerContent={renderRightSideActions()}
      statsConfig={statsConfig}
      totals={summaryTotals}
      statsLoading={summaryLoader}
      formatter={formatNumber}
      tableProps={{
        rowKey: "venue",
        columns: collection_columns,
        data: collectionSummary,
        loading: summaryLoader,
        pagination: false,
      }}
      summary={collectionSummary.length ? renderSummary : undefined}
    />

  )
}

export default CollectionSummary;